import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatTZS, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Smartphone, Compass, Clock, Check, Plus, Minus } from "lucide-react";

const PAYMENT_METHODS = [
  { value: "mpesa", label: "M-Pesa", tag: "Vodacom" },
  { value: "mixx_by_yas", label: "Mixx by Yas", tag: "Yas / Tigo" },
  { value: "airtel_money", label: "Airtel Money", tag: "Airtel" },
  { value: "crdb", label: "CRDB SimBanking", tag: "CRDB Bank" },
  { value: "nmb", label: "NMB Mkononi", tag: "NMB Bank" },
  { value: "offline", label: "Pay at hotel", tag: "Offline" },
];

export default function BookRoom() {
  const { typeId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [type, setType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkIn, setCheckIn] = useState(sp.get("check_in") || new Date().toISOString().slice(0, 10));
  const [checkOut, setCheckOut] = useState(sp.get("check_out") || new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [adults, setAdults] = useState(sp.get("adults") || "2");
  const [children, setChildren] = useState(sp.get("children") || "0");
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [payment, setPayment] = useState("mpesa");
  const [payPhone, setPayPhone] = useState("");
  const [activities, setActivities] = useState([]);
  const [addOns, setAddOns] = useState({}); // { activityId: qty }
  const [discountRule, setDiscountRule] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, { data: acts }, { data: stg }] = await Promise.all([
          api.get(`/room-types/${typeId}`),
          api.get("/activities"),
          api.get("/settings"),
        ]);
        setType(data);
        setActivities(acts || []);
        setDiscountRule(stg?.group_discount || null);
      } catch { toast.error("Room type not found"); navigate("/"); }
    })();
  }, [typeId, navigate]);

  const nights = Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000));
  const guestsTotal = (Number(adults) || 0) + (Number(children) || 0);
  const roomBase = nights * (type?.price_per_night || 0);
  const discountApplies = !!(discountRule?.enabled && (nights >= (discountRule.min_nights || 3) || guestsTotal >= (discountRule.min_guests || 4)));
  const discountPct = discountApplies ? (discountRule?.percent || 0) : 0;
  const discountAmount = discountApplies ? Math.round(roomBase * discountPct / 100) : 0;
  const roomTotal = roomBase - discountAmount;
  const addOnsTotal = activities.reduce((sum, a) => sum + (a.price || 0) * (addOns[a.id] || 0), 0);
  const total = roomTotal + addOnsTotal;

  const toggleAddOn = (id) => setAddOns((prev) => ({ ...prev, [id]: prev[id] ? 0 : 1 }));
  const changeQty = (id, delta) => setAddOns((prev) => {
    const next = Math.max(0, (prev[id] || 0) + delta);
    return { ...prev, [id]: next };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (new Date(checkOut) <= new Date(checkIn)) return toast.error("Check-out must be after check-in");
    setLoading(true);
    try {
      const add_ons = Object.entries(addOns)
        .filter(([, q]) => q > 0)
        .map(([activity_id, quantity]) => ({ activity_id, quantity: Number(quantity) }));
      const { data: booking } = await api.post("/bookings", {
        room_type_id: typeId, guest_name: guestName, guest_email: guestEmail, guest_phone: guestPhone,
        check_in_date: checkIn, check_out_date: checkOut,
        adults: Number(adults), children: Number(children),
        payment_method: payment, notes, add_ons,
      });
      if (payment !== "offline") {
        await api.post("/payments/mobile/initiate", { booking_id: booking.id, method: payment, phone: payPhone || guestPhone });
        toast.success(`Mock ${payment.toUpperCase()} push initiated. Reference ${booking.reference}`);
      } else {
        toast.success(`Booking created. Reference ${booking.reference}`);
      }
      navigate(`/booking/${booking.reference}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (!type) return <div className="min-h-screen flex items-center justify-center bg-[color:var(--sand)]"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand)]" /></div>;

  return (
    <div className="min-h-screen bg-[color:var(--sand)] py-10 px-4 sm:px-8" data-testid="booking-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-ui text-[color:var(--deep)] hover:text-[color:var(--brand)] transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Baraka Inn
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8">
          <form onSubmit={submit} className="bg-white rounded-3xl p-6 sm:p-10 shadow-sm border border-[color:var(--brand)]/10" data-testid="booking-form">
            <h1 className="font-heading text-3xl sm:text-4xl text-[color:var(--deep)]">Complete your booking</h1>
            <p className="font-body text-sm text-[color:var(--muted-text)] mt-2">Fill in your details below.</p>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div><Label className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Check-in</Label><Input type="date" value={checkIn} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setCheckIn(e.target.value)} data-testid="booking-checkin" className="mt-1" /></div>
              <div><Label className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Check-out</Label><Input type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)} data-testid="booking-checkout" className="mt-1" /></div>
              <div><Label className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Adults</Label><Input type="number" min="1" max={type.capacity_adults} value={adults} onChange={(e) => setAdults(e.target.value)} data-testid="booking-adults" className="mt-1" /></div>
              <div><Label className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Children</Label><Input type="number" min="0" max={type.capacity_children} value={children} onChange={(e) => setChildren(e.target.value)} data-testid="booking-children" className="mt-1" /></div>
            </div>

            <h2 className="font-heading text-xl text-[color:var(--deep)] mt-10 mb-4">Guest details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label>Full name</Label><Input required value={guestName} onChange={(e) => setGuestName(e.target.value)} data-testid="guest-name" placeholder="Jina kamili" className="mt-1" /></div>
              <div><Label>Email</Label><Input required type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} data-testid="guest-email" placeholder="you@example.com" className="mt-1" /></div>
              <div><Label>Phone</Label><Input required value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} data-testid="guest-phone" placeholder="+255 712 345 678" className="mt-1" /></div>
              <div className="sm:col-span-2"><Label>Special requests</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="guest-notes" placeholder="Anything we should know?" className="mt-1" /></div>
            </div>

            {activities.length > 0 && (
              <>
                <h2 className="font-heading text-xl text-[color:var(--deep)] mt-10 mb-1 flex items-center gap-2"><Compass className="w-5 h-5 text-[color:var(--brand)]" /> Safari packages &amp; add-ons</h2>
                <p className="font-body text-sm text-[color:var(--muted-text)] mb-1">Optional — we organise everything in advance. Included items are listed on each package.</p>
                <p className="font-ui text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">💡 <b>Prices shown are indicative</b> — final safari / transfer rate is reconfirmed with you at reception based on group size, season and vehicle.</p>
                <div className="space-y-3" data-testid="addons-list">
                  {activities.map((a) => {
                    const qty = addOns[a.id] || 0;
                    const selected = qty > 0;
                    return (
                      <div key={a.id} className={`rounded-2xl border-2 transition-colors overflow-hidden ${selected ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5" : "border-[color:var(--brand)]/15 bg-white"}`} data-testid={`addon-${a.id}`}>
                        <div className="flex gap-3 sm:gap-4 p-3 sm:p-4">
                          {a.image && <img src={a.image} alt={a.title} className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl object-cover flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="font-heading text-base sm:text-lg text-[color:var(--deep)] truncate">{a.title}</p>
                                <div className="flex items-center gap-3 text-xs font-ui text-[color:var(--muted-text)] mt-0.5">
                                  {a.duration && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.duration}</span>}
                                  <span className="font-semibold text-[color:var(--brand)]">{a.price > 0 ? `${formatTZS(a.price)} / person` : "Ask reception"}</span>
                                </div>
                              </div>
                              {!selected ? (
                                <Button type="button" onClick={() => toggleAddOn(a.id)} size="sm" variant="outline" data-testid={`addon-add-${a.id}`} className="rounded-full border-[color:var(--brand)] text-[color:var(--brand)] hover:bg-[color:var(--brand)] hover:text-white flex-shrink-0">
                                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                                </Button>
                              ) : (
                                <div className="flex items-center gap-1 bg-white rounded-full border border-[color:var(--brand)]/30 p-1 flex-shrink-0">
                                  <button type="button" onClick={() => changeQty(a.id, -1)} data-testid={`addon-dec-${a.id}`} className="w-7 h-7 rounded-full hover:bg-[color:var(--sand)] flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                                  <span className="w-6 text-center font-ui text-sm font-semibold" data-testid={`addon-qty-${a.id}`}>{qty}</span>
                                  <button type="button" onClick={() => changeQty(a.id, 1)} data-testid={`addon-inc-${a.id}`} className="w-7 h-7 rounded-full hover:bg-[color:var(--sand)] flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                                </div>
                              )}
                            </div>
                            {(a.includes || []).length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {a.includes.map((inc, i) => (
                                  <span key={i} className="text-[10px] font-ui text-[color:var(--deep)] bg-[color:var(--sand)] border border-[color:var(--brand)]/15 px-1.5 py-0.5 rounded-full flex items-center gap-0.5"><Check className="w-2.5 h-2.5 text-[color:var(--brand)]" /> {inc}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <h2 className="font-heading text-xl text-[color:var(--deep)] mt-10 mb-4">Payment method</h2>
            <RadioGroup value={payment} onValueChange={setPayment} className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="payment-methods">
              {PAYMENT_METHODS.map((m) => (
                <label key={m.value} htmlFor={`pay-${m.value}`} className={`cursor-pointer p-4 rounded-2xl border-2 transition-colors ${payment === m.value ? "border-[color:var(--brand)] bg-[color:var(--brand)]/5" : "border-[color:var(--brand)]/15 bg-white hover:bg-[color:var(--sand)]"}`} data-testid={`payment-${m.value}`}>
                  <RadioGroupItem id={`pay-${m.value}`} value={m.value} className="sr-only" />
                  <div className="flex items-center gap-2"><Smartphone className="w-4 h-4 text-[color:var(--brand)]" /><span className="font-ui font-semibold text-sm text-[color:var(--deep)]">{m.label}</span></div>
                  <p className="mt-1 text-xs font-ui text-[color:var(--muted-text)]">{m.tag}</p>
                </label>
              ))}
            </RadioGroup>

            {payment !== "offline" && (
              <div className="mt-4">
                <Label>Mobile money number (optional)</Label>
                <Input value={payPhone} onChange={(e) => setPayPhone(e.target.value)} placeholder="Leave blank to use your phone above" data-testid="payment-phone" className="mt-1" />
                <p className="text-xs font-ui text-[color:var(--muted-text)] mt-2"><strong>MOCKED:</strong> No real charge will be made. Payment status remains pending until the hotel confirms.</p>
              </div>
            )}

            <Button type="submit" disabled={loading} data-testid="booking-submit" className="mt-8 w-full h-12 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)] text-white font-ui text-base">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm booking · ${formatTZS(total)}`}
            </Button>
          </form>

          <aside className="lg:sticky lg:top-6 h-fit">
            <Card className="overflow-hidden rounded-3xl">
              <img src={type.images?.[0]} alt={type.name} className="w-full h-56 object-cover" />
              <div className="p-6">
                <p className="font-ui text-xs uppercase tracking-wider text-[color:var(--brand)]">Room type</p>
                <h3 className="font-heading text-2xl text-[color:var(--deep)] mt-1">{type.name}</h3>
                <p className="text-sm font-body text-[color:var(--muted-text)] mt-2">{type.description}</p>
                <div className="mt-6 space-y-2 font-ui text-sm">
                  <div className="flex justify-between text-[color:var(--muted-text)]"><span>Rate per night</span><span>{formatTZS(type.price_per_night)}</span></div>
                  <div className="flex justify-between text-[color:var(--muted-text)]"><span>Nights</span><span>{nights}</span></div>
                  <div className="flex justify-between text-[color:var(--muted-text)]"><span>Guests</span><span>{adults}A · {children}C</span></div>
                  <div className="flex justify-between text-[color:var(--muted-text)]"><span>Room base</span><span>{formatTZS(roomBase)}</span></div>
                  {discountApplies && (
                    <div className="flex justify-between text-emerald-700" data-testid="discount-line">
                      <span>{discountRule?.label || "Trekker rate"} −{discountPct}%</span>
                      <span>−{formatTZS(discountAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-[color:var(--deep)]"><span>Room subtotal</span><span data-testid="room-subtotal">{formatTZS(roomTotal)}</span></div>
                  {activities.filter((a) => (addOns[a.id] || 0) > 0).map((a) => (
                    <div key={a.id} className="flex justify-between text-[color:var(--muted-text)] pl-2 text-xs" data-testid={`summary-addon-${a.id}`}>
                      <span className="truncate pr-2">+ {a.title} × {addOns[a.id]}</span>
                      <span>{formatTZS(a.price * addOns[a.id])}</span>
                    </div>
                  ))}
                  {addOnsTotal > 0 && (
                    <div className="flex justify-between text-[color:var(--deep)]"><span>Add-ons subtotal</span><span data-testid="summary-addons-total">{formatTZS(addOnsTotal)}</span></div>
                  )}
                  <div className="border-t pt-3 mt-3 flex justify-between text-[color:var(--deep)] font-semibold text-base">
                    <span>Total</span>
                    <span className="text-[color:var(--brand)] font-heading text-xl" data-testid="booking-total">{formatTZS(total)}</span>
                  </div>
                </div>
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}
